import {CommonModule, isPlatformBrowser} from '@angular/common';
import {ChangeDetectorRef, Component, Inject, Input, OnDestroy, PLATFORM_ID} from '@angular/core';
import {ImageModule} from 'primeng/image';
import {SkeletonModule} from 'primeng/skeleton';
import {MediaObjectInterface} from '../../../core/interfaces/media-object.interface';
import {MediaObjectPipe} from '../../../core/pipes/media-object.pipe';

@Component({
  selector: 'app-comment-media',
  standalone: true,
  imports: [CommonModule, ImageModule, SkeletonModule],
  template: `
    <div *ngIf="media?.length" class="comment-media">
      <div *ngFor="let image of media" class="comment-media__item">
        <p-skeleton *ngIf="!isReady(image)" width="96px" height="96px"></p-skeleton>
        <p-image [style.visibility]="isReady(image) ? 'visible' : 'hidden'"
          [src]="imageUrl(image)"
          [previewImageSrc]="imageUrl(image)"
          [alt]="image.originalName || image.name || ''"
          width="96" height="96" [preview]="isReady(image)" appendTo="body" loading="lazy">
        </p-image>
      </div>
    </div>
  `,
  styles: [`
    .comment-media { display: flex; flex-wrap: wrap; gap: .5rem; margin: .5rem 0; }
    .comment-media__item { width: 96px; height: 96px; position: relative; }
    .comment-media__item p-skeleton, .comment-media__item p-image { position: absolute; inset: 0; }
    :host ::ng-deep .comment-media__item .p-image-preview-container > img { width: 96px; height: 96px; object-fit: cover; border-radius: .5rem; }
  `]
})
export class CommentMediaComponent implements OnDestroy {
  private _media: MediaObjectInterface[] = [];
  private readonly readyUrls = new Set<string>();
  private readonly loadingUrls = new Set<string>();
  private readonly retryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly preloaders = new Map<string, HTMLImageElement>();
  private destroyed = false;

  constructor(@Inject(PLATFORM_ID) private readonly platformId: Object, private readonly cdr: ChangeDetectorRef) {}

  @Input() set media(value: MediaObjectInterface[]) {
    this._media = value || [];
    if (isPlatformBrowser(this.platformId)) {
      this._media.forEach(image => this.preload(image));
    }
  }

  get media(): MediaObjectInterface[] {
    return this._media;
  }

  imageUrl(image: MediaObjectInterface): string {
    return MediaObjectPipe.transformToLink(image, {formatType: 'thumb_md', defaultImageType: 'loading'});
  }

  isReady(image: MediaObjectInterface): boolean {
    return this.readyUrls.has(this.imageUrl(image));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.retryTimers.forEach(timer => clearTimeout(timer));
    this.preloaders.forEach(preloader => {
      preloader.onload = null;
      preloader.onerror = null;
    });
    this.retryTimers.clear();
    this.preloaders.clear();
  }

  private preload(image: MediaObjectInterface, attempt = 0): void {
    const url = this.imageUrl(image);
    if (!url || this.readyUrls.has(url) || this.loadingUrls.has(url) || this.destroyed) {
      return;
    }

    this.loadingUrls.add(url);
    const preloader = new Image();
    this.preloaders.set(url, preloader);
    preloader.onload = () => {
      this.loadingUrls.delete(url);
      this.preloaders.delete(url);
      this.readyUrls.add(url);
      this.cdr.markForCheck();
    };
    preloader.onerror = () => {
      this.loadingUrls.delete(url);
      this.preloaders.delete(url);
      if (attempt < 7 && !this.destroyed) {
        const timer = setTimeout(() => {
          this.retryTimers.delete(url);
          this.preload(image, attempt + 1);
        }, 1500);
        this.retryTimers.set(url, timer);
      }
    };
    preloader.src = url;
  }
}
